/**
 * What is on this device and has not reached the server.
 *
 * THE PANEL MAKES ONE DISTINCTION AND EVERYTHING ELSE SERVES IT: an item
 * WAITING will send itself the moment there is a connection, and an item
 * BLOCKED never will. Both are "not saved yet" and they look identical in a
 * count, but one needs patience and the other needs a person — and somebody
 * standing in a plant room deciding whether to walk the round again is
 * entitled to know which they are looking at. So they are separate sections,
 * with different colours, different wording, and different buttons.
 *
 * IT DOES NOT REPLACE THE TOAST. The toast tells you at the moment you queue
 * something; this tells you afterwards, for as long as it is true. Neither is
 * sufficient alone — a toast fades while the work is still unsent, and a badge
 * nobody thought to open says nothing at the moment it matters.
 *
 * IT IS HONEST ABOUT ITS SCOPE. This is one browser's localStorage. Work
 * queued on the technician's phone is not here, and the panel says so rather
 * than letting a supervisor read "0 waiting" as "nothing outstanding anywhere".
 */

import { Fragment, useState } from 'react';

/** Why a panel action is off while another one runs (review R2-5 #3). */
export const ANOTHER_SEND = 'Another send is running — wait for it to finish';
/** A move the device could not store (review R2-5 #1). */
export const NO_ROOM_TO_MOVE = 'This device has no room to move it — nothing was moved.';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  PhotoIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  BEING_SENT,
  EARLIER_SIGN_IN,
  discardBlocked,
  flush,
  getPending,
  isInFlight,
  moveToBlocked,
  retryBlocked,
  retryOne,
  type BlockedItem,
  type PendingItem,
} from '@/services/pendingQueue';
import { ageOf, usePendingQueue } from '@/components/Common/usePendingQueue';
import ModalScrollContainer from './ModalScrollContainer';
import { display } from '@/utils/dateDisplay';

function Meta({ item }: { item: PendingItem }) {
  return (
    <p className="mt-0.5 text-[11px] text-content-subtle">
      <span title={display(item.capturedAt, 'dateTime')}>
        Captured {ageOf(item.capturedAt)}
      </span>
      {item.attempts > 0 && (
        <> · {item.attempts} {item.attempts === 1 ? 'attempt' : 'attempts'}</>
      )}
      {item.blobRef && (
        <> · <PhotoIcon className="inline h-3 w-3 align-[-2px]" /> photo</>
      )}
    </p>
  );
}

function Row({
  item, tone, note, children,
}: {
  item: PendingItem;
  tone: 'waiting' | 'blocked';
  /** Said beside the actions — why one of them is off. */
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-content">{item.label}</p>
          <Meta item={item} />
          {item.lastError && (
            <p
              className={`mt-1 text-xs ${
                tone === 'blocked' ? 'text-danger-700' : 'text-content-muted'
              }`}
            >
              {/* FM Technician A16: a refusal reads "Not accepted: <the
                  server's reason>"; an ownerless item from before the queue
                  was kept per person says so in its own words instead. */}
              {(item as BlockedItem).fromEarlierSignIn
                ? EARLIER_SIGN_IN
                : (item as BlockedItem).heldBack
                  ? item.lastError
                  : <>{tone === 'blocked' ? 'Not accepted: ' : 'Last try: '}{item.lastError}</>}
            </p>
          )}
          {note && (
            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-brand-700">
              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              {note}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">{children}</div>
      </div>
    </li>
  );
}

export default function PendingQueuePanel({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { waiting, blocked } = usePendingQueue();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<BlockedItem | null>(null);
  // R2-3 #2: a waiting item the person moves to Not sent, so work behind it can go.
  const [moving, setMoving] = useState<PendingItem | null>(null);
  // R2-4 #4: why the move could not be done, said in the dialog rather than
  // closing it on nothing.
  const [moveRefused, setMoveRefused] = useState<string | null>(null);
  const movingOut = moving !== null && isInFlight(moving.id);
  const askToMove = (w: PendingItem | null) => { setMoveRefused(null); setMoving(w); };

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full" enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0" leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto fixed inset-y-0 right-0 w-full max-w-sm">
                <div className="flex h-full flex-col bg-surface shadow-xl">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-line px-4 py-4">
                    <Dialog.Title className="text-lg font-semibold text-content">
                      Unsent work
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="rounded-lg p-1.5 text-content-muted hover:bg-surface-muted"
                      aria-label="Close unsent work"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {waiting.length === 0 && blocked.length === 0 && (
                      <div className="px-4 py-10 text-center">
                        <CheckCircleIcon className="mx-auto h-8 w-8 text-success-500" />
                        <p className="mt-2 text-sm font-medium text-content">
                          Everything captured on this device has been sent
                        </p>
                      </div>
                    )}

                    {/* ---- NEEDS A PERSON. First, because it is the section
                            that will still be here tomorrow if nobody acts. */}
                    {blocked.length > 0 && (
                      <section aria-labelledby="queue-blocked">
                        <div className="flex items-start gap-2 border-b border-danger-500/30 bg-danger-50 px-4 py-3 dark:bg-danger-950">
                          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-danger-700 dark:text-danger-300" />
                          <div>
                            <h3 id="queue-blocked" className="text-sm font-semibold text-danger-900 dark:text-danger-100">
                              {blocked.length} {blocked.length === 1 ? 'item needs' : 'items need'} attention
                            </h3>
                            <p className="mt-0.5 text-[11px] text-danger-800 dark:text-danger-200">
                              The server read {blocked.length === 1 ? 'this' : 'these'} and refused
                              {blocked.length === 1 ? ' it' : ' them'}. Waiting will not help —
                              fix what it objected to and try again, or discard.
                            </p>
                          </div>
                        </div>
                        <ul className="divide-y divide-line-subtle">
                          {blocked.map((b) => (
                            <Row key={b.id} item={b} tone="blocked">
                              <button
                                onClick={() => run(b.id, () => retryBlocked(b.id))}
                                disabled={busy === b.id}
                                className="rounded-lg p-1.5 text-content-muted hover:bg-surface-muted disabled:opacity-50"
                                aria-label={`Try "${b.label}" again`}
                                title="Try again"
                              >
                                <ArrowPathIcon className={`h-4 w-4 ${busy === b.id ? 'animate-spin' : ''}`} />
                              </button>
                              <button
                                onClick={() => setConfirming(b)}
                                className="rounded-lg p-1.5 text-danger-600 hover:bg-danger-50"
                                aria-label={`Discard "${b.label}"`}
                                title="Discard"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </Row>
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* ---- WAITING. Nothing to do; it goes on its own. */}
                    {waiting.length > 0 && (
                      <section aria-labelledby="queue-waiting">
                        <div className="flex items-center justify-between gap-2 border-b border-line bg-surface-subtle px-4 py-3">
                          <div className="flex items-start gap-2">
                            <CloudArrowUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-content-muted" />
                            <div>
                              <h3 id="queue-waiting" className="text-sm font-semibold text-content">
                                {waiting.length} waiting to send
                              </h3>
                              <p className="mt-0.5 text-[11px] text-content-subtle">
                                Saved here. {waiting.length === 1
                                  ? 'It goes on its own'
                                  : 'They go on their own'} when the connection is back.
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => run('all', () => flush())}
                            disabled={busy !== null}
                            title={busy !== null ? ANOTHER_SEND : undefined}
                            className="shrink-0 rounded-lg bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                          >
                            <ArrowPathIcon
                              className={`mr-1 inline h-3.5 w-3.5 ${busy === 'all' ? 'animate-spin' : ''}`}
                            />
                            Try all
                          </button>
                        </div>
                        <ul className="divide-y divide-line-subtle">
                          {waiting.map((w) => (
                            <Row key={w.id} item={w} tone="waiting" note={isInFlight(w.id) ? BEING_SENT : undefined}>
                              <button
                                onClick={() => run(w.id, () => retryOne(w.id))}
                                disabled={busy !== null}
                                className="rounded-lg p-1.5 text-content-muted hover:bg-surface-muted disabled:opacity-50"
                                aria-label={`Try "${w.label}" again`}
                                title={busy !== null && busy !== w.id ? ANOTHER_SEND : 'Try again'}
                              >
                                <ArrowPathIcon className={`h-4 w-4 ${busy === w.id ? 'animate-spin' : ''}`} />
                              </button>
                              <button
                                onClick={() => askToMove(w)}
                                disabled={busy !== null || isInFlight(w.id)}
                                className="rounded-lg p-1.5 text-content-muted hover:bg-surface-muted disabled:opacity-50"
                                aria-label={`Move "${w.label}" to Not sent`}
                                title={isInFlight(w.id) ? BEING_SENT : busy !== null ? ANOTHER_SEND : 'Move to Not sent'}
                              >
                                <NoSymbolIcon className="h-4 w-4" />
                              </button>
                            </Row>
                          ))}
                        </ul>
                      </section>
                    )}
                  </div>

                  {/* ---- What this panel cannot tell you.
                          Load-bearing, not a disclaimer: a supervisor reading
                          "nothing waiting" as "nothing outstanding anywhere"
                          is a worse error than not having the panel. */}
                  <p className="border-t border-line px-4 py-3 text-[11px] text-content-subtle">
                    This device only. Work saved on another phone, tablet or kiosk is not
                    visible here — each device holds and sends its own.
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

        {/* R2-3 #2: moving a waiting item out of the way. Kept, not deleted —
            it can be sent again from "needs attention". */}
        <Transition show={moving !== null} as={Fragment}>
          <Dialog as="div" className="relative z-[60]" onClose={() => askToMove(null)}>
            <div className="fixed inset-0 bg-scrim/40" />
            <ModalScrollContainer>
              <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-surface p-5 shadow-xl">
                <Dialog.Title className="text-base font-semibold text-content">
                  Move to Not sent?
                </Dialog.Title>
                <p className="mt-2 text-sm text-content-secondary">
                  “{moving?.label}” stops being sent on its own, so the work saved after it
                  can go. It stays on this device under “needs attention”, where you can
                  try it again or discard it.
                </p>
                {(movingOut || moveRefused) && (
                  <p role="status" className="mt-3 text-sm font-medium text-warning-700">
                    {movingOut ? BEING_SENT : moveRefused}
                  </p>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    onClick={() => askToMove(null)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-content-secondary hover:bg-surface-muted"
                  >
                    Keep waiting
                  </button>
                  <button
                    onClick={() => {
                      if (!moving) return;
                      // R2-4 #4: refused while its request is out (it might
                      // land) — the dialog stays open and says so, instead of
                      // closing as if it had been moved.
                      if (!moveToBlocked(moving.id)) {
                        // Why it was refused, said truly (review R2-5 #1): still out,
                        // still waiting but the device had no room to move it,
                        // or gone (sent or moved meanwhile).
                        setMoveRefused(isInFlight(moving.id) ? BEING_SENT
                          : getPending().some((i) => i.id === moving.id) ? NO_ROOM_TO_MOVE
                            : 'It is no longer waiting — nothing was moved.');
                        return;
                      }
                      askToMove(null);
                      // What was behind it goes now, in order.
                      void flush();
                    }}
                    disabled={movingOut}
                    className="rounded-lg bg-brand-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    Move to Not sent
                  </button>
                </div>
              </Dialog.Panel>
            </ModalScrollContainer>
          </Dialog>
        </Transition>

        {/* Discarding is the one action here that destroys a capture, so it
            names what is about to go and does not offer a bulk version. */}
        <Transition show={confirming !== null} as={Fragment}>
          <Dialog as="div" className="relative z-[60]" onClose={() => setConfirming(null)}>
            <div className="fixed inset-0 bg-scrim/40" />
            <ModalScrollContainer>
              <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-surface p-5 shadow-xl">
                <Dialog.Title className="text-base font-semibold text-content">
                  Discard this capture?
                </Dialog.Title>
                <p className="mt-2 text-sm text-content-secondary">
                  “{confirming?.label}” was never accepted by the server. Discarding
                  removes it from this device
                  {confirming?.blobRef ? ', along with its photo,' : ''} and it cannot be
                  recovered.
                </p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    onClick={() => setConfirming(null)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-content-secondary hover:bg-surface-muted"
                  >
                    Keep it
                  </button>
                  <button
                    onClick={() => {
                      if (confirming) discardBlocked(confirming.id);
                      setConfirming(null);
                    }}
                    className="rounded-lg bg-danger-600 px-3 py-2 text-sm font-semibold text-white hover:bg-danger-700"
                  >
                    Discard
                  </button>
                </div>
              </Dialog.Panel>
            </ModalScrollContainer>
          </Dialog>
        </Transition>
      </Dialog>
    </Transition>
  );
}
